<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddContactReason from './AddContactReason.vue';
import EditContactReason from './EditContactReason.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedReason = ref({});

const records = computed(
  () => getters['contactReasons/getContactReasons'].value
);
const uiFlags = computed(() => getters['contactReasons/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedReason.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedReason.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedReason.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteReason = async id => {
  try {
    await store.dispatch('contactReasons/delete', id);
    useAlert(t('CONTACT_REASONS_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CONTACT_REASONS_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedReason.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedReason.value.id] = true;
  closeDeletePopup();
  deleteReason(selectedReason.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('CONTACT_REASONS_MGMT.LIST.TABLE_HEADER.NAME'),
    t('CONTACT_REASONS_MGMT.LIST.TABLE_HEADER.CATEGORY'),
    t('CONTACT_REASONS_MGMT.LIST.TABLE_HEADER.COLOR'),
    t('CONTACT_REASONS_MGMT.LIST.TABLE_HEADER.STATUS'),
  ];
});

onBeforeMount(() => {
  store.dispatch('contactReasons/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CONTACT_REASONS_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CONTACT_REASONS_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CONTACT_REASONS_MGMT.HEADER')"
        :description="$t('CONTACT_REASONS_MGMT.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CONTACT_REASONS_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="reason in records" :key="reason.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ reason.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ reason.category || '-' }}
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center">
                <span
                  class="w-4 h-4 mr-1 border border-solid rounded rtl:mr-0 rtl:ml-1 border-n-weak"
                  :style="{ backgroundColor: reason.color || '#4A6CF7' }"
                />
                {{ reason.color || '#4A6CF7' }}
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  reason.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  reason.active
                    ? $t('CONTACT_REASONS_MGMT.STATUS.ACTIVE')
                    : $t('CONTACT_REASONS_MGMT.STATUS.INACTIVE')
                }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('CONTACT_REASONS_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openEditPopup(reason)"
                />
                <Button
                  v-tooltip.top="$t('CONTACT_REASONS_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openDeletePopup(reason)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddContactReason @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditContactReason
        :selected-response="selectedReason"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CONTACT_REASONS_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CONTACT_REASONS_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('CONTACT_REASONS_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('CONTACT_REASONS_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
